import { ReactComponent as UpdateGlyph } from '@/assets/images/svg/glyphs/update.svg';
import Heading from '@/components/_atoms/Heading';
import { PrimaryDefaultButton } from '@/components/buttons/primary';
import { Container } from '@/components/layout/Container';
import { SupportReportQuery } from '@/graphql';
import SupportReportQueryDocument from '@/graphql/queries/SupportReport.gql';
import useBreakpoint from '@/lib/hooks/useBreakpoint';
import { getStaticPageProps } from '@/lib/next';
import { parseHtml } from '@/lib/parse-html';
import { maybeNotSet } from '@/lib/typescript-helpers';
import FeatureGroups from '@/modules/FeatureGroups';
import ResourceCards from '@/modules/ResourceCards';
import SimpleCTAFooterModule from '@/modules/SimpleCTAFooter';
import SimpleHeroModule from '@/modules/SimpleHero';
import SocialProofCardCarousel from '@/modules/SocialProofCardCarousel';
import TextColumnsModule from '@/modules/TextColumns';
import { COLORS } from '@/styles/color';
import { hexToRGBA } from '@/styles/color/utils';
import {
  applyBreakpointNegativeMargins,
  applyBreakpointPadding,
  mediaBreakpointOnly,
  mediaBreakpointUp,
  paddingX,
  paddingY,
} from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import MasterTemplate from '@/templates/MasterTemplate';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { InferGetStaticPropsType } from 'next';
import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import CountUp from 'react-countup';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, YAxisProps } from 'recharts';
import styled from 'styled-components';

dayjs.extend(relativeTime);

const SupportReportPage: PageTemplate<InferGetStaticPropsType<typeof getStaticProps>> = ({
  data,
  lastUpdated,
  entry,
}) => {
  const [emailState, setEmailState] = useState<'all' | 'first'>('all');
  const [chatState, setChatState] = useState<'all' | 'first'>('all');

  const formattingFn = useCallback((number: number) => secondsToHours(number).formatted, []);

  if (!entry) return null;

  return (
    <PageWrapper>
      <SimpleHeroModule props={entry?.moduleSimplehero} />

      <Container>
        <LastUpdated>
          <UpdateGlyph width={20} />
          Last updated {dayjs().to(dayjs(lastUpdated))}
        </LastUpdated>

        <Chart
          heading={maybeNotSet(chatState === 'all' ? entry.chatAllHeading : entry.chatFirstHeading)}
          description={parseHtml(chatState === 'all' ? entry.chatAllDescription : entry.chatFirstDescription)}
          benchmark={parseHtml(chatState === 'all' ? entry.chatAllBenchmark : entry.chatFirstBenchmark)}
          periodAverage={
            <CountUp
              start={data.averages.chat}
              end={chatState === 'all' ? data.averages.chat : data.averages.firstChat}
              duration={1}
              formattingFn={formattingFn}
              useEasing
              preserveValue
            />
          }
          data={chatState === 'all' ? data.chatResponse : data.chatFirstResponse}
          lineLabel="Average Response Time"
          yAxisLabel="Minutes"
          toggleGroup={
            <ToggleGroup>
              <PrimaryDefaultButton
                data-active={chatState === 'all' ? '' : undefined}
                onClick={() => setChatState('all')}
              >
                All responses
              </PrimaryDefaultButton>
              <PrimaryDefaultButton
                data-active={chatState === 'first' ? '' : undefined}
                onClick={() => setChatState('first')}
              >
                First response
              </PrimaryDefaultButton>
            </ToggleGroup>
          }
        />

        <Chart
          heading={maybeNotSet(emailState === 'all' ? entry.emailAllHeading : entry.emailFirstHeading)}
          description={parseHtml(emailState === 'all' ? entry.emailAllDescription : entry.emailFirstDescription)}
          benchmark={parseHtml(emailState === 'all' ? entry.emailAllBenchmark : entry.emailFirstBenchmark)}
          periodAverage={
            <CountUp
              start={data.averages.email}
              end={emailState === 'all' ? data.averages.email : data.averages.firstEmail}
              duration={1}
              formattingFn={formattingFn}
              useEasing
              preserveValue
            />
          }
          data={emailState === 'all' ? data.emailResponse : data.emailFirstResponse}
          lineLabel="Average Response Time"
          yAxisLabel="Hours"
          toggleGroup={
            <ToggleGroup>
              <PrimaryDefaultButton
                data-active={emailState === 'all' ? '' : undefined}
                onClick={() => setEmailState('all')}
              >
                All responses
              </PrimaryDefaultButton>
              <PrimaryDefaultButton
                data-active={emailState === 'first' ? '' : undefined}
                onClick={() => setEmailState('first')}
              >
                First response
              </PrimaryDefaultButton>
            </ToggleGroup>
          }
        />

        <Chart
          heading={maybeNotSet(entry.csatHeading)}
          description={parseHtml(entry.csatDescription)}
          benchmark={parseHtml(entry.csatBenchmark)}
          periodAverage={data.averages.csat}
          data={data.csat}
          lineLabel="Score"
          yAxisLabel="Score"
          yAxisProps={{
            domain: [0, 100],
            ticks: [0, 20, 40, 60, 80, 100],
            tickFormatter: (tick) => `${tick}%`,
          }}
          yAxisWidth={70}
          noDataMessage={
            <>
              No CSAT surveys were
              <br />
              taken on this day
            </>
          }
        />
      </Container>

      <ModulesWrapper>
        <FeatureGroups props={entry?.moduleFeatureGroups} />

        <StyledSocialProofCardCarousel props={entry?.moduleSocialProofCardCarousel} />

        <Container>
          <StyledTextColumnsModule props={entry?.moduleTextColumns} />
        </Container>

        <Container>
          <ResourceCards props={entry?.moduleResourceCards} />
        </Container>

        <SimpleCTAFooterModule props={entry?.moduleSimplectafooter} />
      </ModulesWrapper>

      <SourcesFooter id={'sources'}>
        <Container>{parseHtml(entry.sourcesFooter)}</Container>
      </SourcesFooter>
    </PageWrapper>
  );
};

SupportReportPage.template = MasterTemplate;

export default SupportReportPage;

export const getStaticProps = getStaticPageProps('support-report', async ({ client }) => {
  const props = await client.request<SupportReportQuery>(SupportReportQueryDocument);
  const response = await fetch('https://app.frontapp.com/api/1/noauth/support_metrics');

  if (!response.ok) {
    throw new Error(response.statusText);
  }

  const data = (await response.json()) as RawMetricsResponse;
  const lastUpdated = dayjs(data.last_refresh).toISOString();

  const formatMetrics =
    (multiplier: number = 1) =>
    (metric: RawDailyMetric) => {
      const date = dayjs(metric.name).year(dayjs().year());

      const value = metric.value * multiplier;
      const { formatted: formattedValue } = secondsToHours(metric.value);

      return {
        value,
        formattedValue,
        displayDate: date.format('MMM D'),
        displayDateFull: date.format('dddd, MMMM D'),
      };
    };

  const emailResponse = data.email.avg_graph.map(formatMetrics(1 / 60 / 60));
  const emailFirstResponse = data.email.first_response_avg_graph.map(formatMetrics(1 / 60 / 60));
  const chatResponse = data.chat.avg_graph.map(formatMetrics(1 / 60));
  const chatFirstResponse = data.chat.first_response_avg_graph.map(formatMetrics(1 / 60));
  const csat = data.csat.avg_graph_pct.map(formatMetrics()).map((metric) => {
    metric.value = metric.value * 100;
    metric.formattedValue = `${metric.value.toFixed(0)}%`;

    if (metric.value === 0) {
      // @ts-ignore
      metric.value = null;
    }

    return metric;
  });

  const averages = {
    email: data.email.avg_value,
    firstEmail: data.email.first_response_avg_value,
    chat: data.chat.avg_value,
    firstChat: data.chat.first_response_avg_value,
    csat: `${(data.csat.avg_value * 100).toPrecision(2)}%`,
  };

  return {
    props: {
      data: {
        averages,
        emailResponse,
        emailFirstResponse,
        chatResponse,
        chatFirstResponse,
        csat,
      },
      entry: props.entry,
      lastUpdated,
    },
    revalidate: 60 * 60 * 24, // 24hrs
  };
});

const secondsToHours = (totalSeconds: number) => {
  const totalMinutes = Math.floor(totalSeconds / 60);

  const seconds = Math.floor(totalSeconds % 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.floor(totalMinutes % 60);

  let formatted = `${minutes}m ${seconds}s`;

  if (hours) {
    formatted = `${hours}h ${formatted}`;
  }

  return {
    hours,
    minutes,
    seconds,
    formatted,
  };
};

interface RawMetricsResponse {
  email: {
    avg_value: number;
    first_response_avg_value: number;
    avg_graph: RawDailyMetric[];
    first_response_avg_graph: RawDailyMetric[];
  };
  chat: {
    avg_value: number;
    first_response_avg_value: number;
    avg_graph: RawDailyMetric[];
    first_response_avg_graph: RawDailyMetric[];
  };
  csat: {
    avg_value: number;
    avg_graph: RawDailyMetric[];
    avg_graph_pct: RawDailyMetric[];
  };
  last_refresh: string;
}

type RawDailyMetric = {
  name: string;
  value: number;
};

// Components

const PageWrapper = styled.div`
  position: relative;

  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 800px;
    left: 0px;
    top: 0;
    z-index: -1;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 100.37%),
      radial-gradient(91.91% 112.33% at 41.49% 114.56%, #ffbfda 47.67%, rgba(255, 255, 255, 0) 100%),
      radial-gradient(114.27% 131.25% at 141.39% 30.02%, #ffecb3 49.4%, rgba(255, 191, 218, 0) 97.72%),
      radial-gradient(77.92% 104.05% at 4.1% 50.24%, #b0f1fc 62.86%, rgba(217, 248, 255, 0) 99.6%), #ffffff;
  }
`;

const TableWrapper = styled.div`
  box-shadow: 0px 0px 20px 0px rgba(0, 27, 56, 0.1);
  border-radius: 10px;
  background: ${COLORS.WHITE};

  ${mediaBreakpointOnly('mobile')} {
    ${applyBreakpointNegativeMargins};
  }

  & + & {
    margin-top: 50px;
  }

  ${mediaBreakpointUp('desktop')} {
    & + & {
      margin-top: 80px;
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    & + & {
      margin-top: 120px;
    }
  }
`;

const TableHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  ${paddingY(40)};
  ${applyBreakpointPadding};
  border-bottom: 1px solid #e3e3e3;
  text-align: center;

  a {
    text-decoration: underline;
  }

  sub,
  sup {
    padding: 0 1px;
  }

  > div:first-of-type {
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex-grow: 1;
  }

  ${mediaBreakpointUp('tablet')} {
    padding: ${remCalc(40)};
  }

  ${mediaBreakpointUp('desktop')} {
    text-align: left;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;

    p {
      max-width: 60%;
    }
  }
`;

const Benchmark = styled.div`
  display: flex;
  align-items: center;
  margin: 0 auto;

  span {
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  }

  svg {
    display: inline-block;
    margin-right: ${remCalc(8)};
    width: 20px;
    height: auto;
  }

  ${mediaBreakpointUp('desktop')} {
    padding-top: ${remCalc(14)};
    margin: 0;
  }
`;

const TableContainer = styled.div`
  ${paddingY(40)};
  ${paddingX(15)};

  ${mediaBreakpointUp('tablet')} {
    padding: ${remCalc(40)};
  }
`;

const SourcesFooter = styled.section`
  ${customTextSize(14, 20)};
  background: ${COLORS.GRAY.TINT};
  padding: 2.75rem 0;
  * + ol,
  ol > li + li {
    margin-top: 4px;
  }
  ol {
    list-style-type: decimal;
    padding-left: 1rem;
    li {
      a {
        text-decoration: underline;
      }
    }
  }
`;

const CurrentMetric = styled.p`
  display: flex;
  flex-direction: column-reverse;
  gap: 10px;
  ${customTextSize(16, 20)};
  font-weight: ${FONT_WEIGHTS.BOLD};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: ${remCalc(40)};
  text-align: center;

  strong {
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    ${customTextSize(49, 58)}
    letter-spacing: -0.01em;
    text-transform: none;
  }

  ${mediaBreakpointUp('desktop')} {
    text-align: left;
  }
`;

const LastUpdated = styled.p`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin: 0 0 2rem 0;
  gap: 10px;
`;

const ToggleGroup = styled.div`
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  align-self: center;

  ${PrimaryDefaultButton} {
    --button-color: ${COLORS.WHITE};
    --button-text-color: ${COLORS.BLACK};
    --button-border-color: ${COLORS.BLACK};
    --button-color-hover: ${COLORS.BLACK};

    border-width: 1px;

    &[data-active] {
      --button-color: ${COLORS.BLACK};
      --button-text-color: ${COLORS.WHITE};
    }

    &:first-of-type {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      border-right-width: 0;
    }

    &:last-of-type {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      border-left-width: 0;
    }
  }

  ${mediaBreakpointUp('desktop')} {
    align-self: auto;
  }
`;

const TooltipWrapper = styled.div`
  box-shadow: 0px 0px 10px 0px ${hexToRGBA(COLORS.BLACK, 0.25)};
  border-radius: 10px;
  background: ${COLORS.WHITE};
  padding: 16px 16px 12px 16px;

  > p {
    text-transform: uppercase;
    ${customTextSize(12, 20)};
    letter-spacing: 0.1em;
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    margin: 0 0 0.25rem 0;
  }

  li {
    ${customTextSize(16, 24)};
  }
`;

const StyledSocialProofCardCarousel = styled(SocialProofCardCarousel)`
  ${mediaBreakpointUp('desktop')} {
    --social-proof-carousel-card-min-height: 420px;
  }
`;

const StyledTextColumnsModule = styled(TextColumnsModule)`
  --text-columns-text-alignment: center;
  --text-columns-flex-alignment: center;
  --text-columns-count: 4;
`;

const ModulesWrapper = styled.div`
  &,
  & > * + *,
  & > ${Container} > * + * {
    margin-top: 50px;
  }

  ${mediaBreakpointUp('desktop')} {
    &,
    & > * + *,
    & > ${Container} > * + * {
      margin-top: 80px;
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    &,
    & > * + *,
    & > ${Container} > * + * {
      margin-top: 120px;
    }
  }
`;

interface ChartProps {
  heading: string;
  description?: ReactNode;
  benchmark?: ReactNode;
  periodAverage: ReactNode;
  data: {
    value: string | number;
    formattedValue: string | number;
    displayDate: string;
    displayDateFull: string;
  }[];
  lineLabel: string;
  yAxisLabel: string;
  yAxisProps?: YAxisProps;
  yAxisWidth?: number;
  toggleGroup?: ReactNode;
  noDataMessage?: ReactNode;
}

const Chart: FC<ChartProps> = ({
  heading,
  description,
  benchmark,
  periodAverage,
  data,
  yAxisLabel,
  lineLabel,
  yAxisProps,
  yAxisWidth = 45,
  toggleGroup,
  noDataMessage,
}) => {
  const [currentBreakpoint] = useBreakpoint();

  const ticks = useMemo(() => {
    const totalDays = data.length;
    const middleDay = Math.round(totalDays / 2);
    const oneQuarter = Math.round(middleDay / 2);
    const oneEighth = Math.round(totalDays / 8);

    let daysToShow: number[] = [];

    switch (currentBreakpoint) {
      case 'mobile':
        daysToShow = [middleDay];
        break;
      case 'tablet':
      case 'desktop':
        daysToShow = [oneQuarter, middleDay, middleDay + oneQuarter];
        break;
      default:
        daysToShow = [
          oneEighth,
          oneQuarter,
          oneEighth + oneQuarter,
          middleDay,
          middleDay + oneEighth,
          middleDay + oneQuarter,
          middleDay + oneQuarter + oneEighth,
        ];
    }

    return [1, ...daysToShow, totalDays].map((day) => data[day - 1].displayDate);
  }, [data, currentBreakpoint]);

  return (
    <TableWrapper>
      <TableHeading>
        <div>
          <Heading level={3}>{heading}</Heading>
          {description}
          <Benchmark>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M11.872 8.76116C12.688 8.76116 13.344 8.09455 13.344 7.28509C13.344 6.47564 12.688 5.8249 11.872 5.8249C11.056 5.8249 10.4 6.49151 10.4 7.28509C10.4 8.07867 11.056 8.76116 11.872 8.76116ZM13.44 10.8086C13.44 10.142 12.896 9.61823 12.24 9.61823H11.04C10.368 9.61823 9.84 10.142 9.84 10.8086C9.84 11.4752 10.368 11.999 11.04 11.999V15.4749C10.368 15.4749 9.84 16.0145 9.84 16.6652C9.84 17.316 10.368 17.8556 11.04 17.8556H13.44C14.096 17.8556 14.64 17.3319 14.64 16.6652C14.64 15.9986 14.096 15.4749 13.44 15.4749V10.8086ZM12 0C18.624 0 24 5.33288 24 11.9037C24 18.4746 18.624 23.8075 12 23.8075C5.376 23.8075 0 18.4746 0 11.9037C0 5.33288 5.36 0 12 0Z"
                fill={COLORS.BLACK}
              />
            </svg>
            <span>{benchmark}</span>
          </Benchmark>
        </div>

        {toggleGroup}
      </TableHeading>

      <TableContainer>
        <CurrentMetric>
          90 Day Average <strong>{periodAverage}</strong>
        </CurrentMetric>

        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            height={300}
            data={data}
            margin={{
              left: 0,
              right: 0,
              bottom: 0,
              top: 4,
            }}
            syncId="chart"
          >
            <XAxis
              dataKey="displayDate"
              ticks={ticks}
              minTickGap={0}
              interval={0}
              padding={{
                left: 24,
                right: 24,
              }}
              stroke={COLORS.BLACK}
              fill={COLORS.BLACK}
              tickLine={false}
              height={30}
            />

            <YAxis
              allowDecimals={false}
              label={{
                value: yAxisLabel.toUpperCase(),
                style: {
                  textAnchor: 'middle',
                  fill: COLORS.BLACK,
                  letterSpacing: '0.1em',
                  ...customTextSize(12, 20),
                  fontWeight: FONT_WEIGHTS.SEMIBOLD,
                },
                angle: -90,
                position: 'left',
                offset: -5,
              }}
              stroke={COLORS.BLACK}
              fill={COLORS.BLACK}
              tickLine={false}
              width={yAxisWidth}
              {...yAxisProps}
            />

            <Tooltip
              filterNull={false}
              content={(props) => {
                const dataValue = props.payload?.[0]?.value;

                if (!props.active || !props.payload || (!dataValue && !noDataMessage)) return null;

                return (
                  <TooltipWrapper>
                    <p>{props.payload[0].payload.displayDateFull}</p>

                    {!dataValue ? (
                      noDataMessage
                    ) : (
                      <ul>
                        {props.payload.map((item) => (
                          <li key={item.name}>
                            {item.name}: {item.payload.formattedValue}
                          </li>
                        ))}
                      </ul>
                    )}
                  </TooltipWrapper>
                );
              }}
            />

            <Line
              type="monotone"
              name={lineLabel}
              dataKey="value"
              stroke={COLORS.BLACK}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 5 }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </TableContainer>
    </TableWrapper>
  );
};
